import * as React from 'react';
import Page from '../../components/Page';
import { ProfileContext } from '../../profile';
import { SalesReport, LineItem } from '../../sales-report';
import * as dateFormat from 'dateformat';

interface Props {
  report: SalesReport;
}

const DATE_FORMAT = 'dd, mmmm yyyy';

const getPrice = (value: number, currency: string) => (
  <React.Fragment>
    {currency}&nbsp;{(value / 100).toFixed(2)}
  </React.Fragment>
);

export default function SalesReport(props: Props) {
  const context = React.useContext(ProfileContext);
  const contract = context.profile.company.contract;

  return (
    <Page
      title={`${dateFormat(
        props.report.period_start,
        'mmmm yyyy',
      )} Report`}
      subtitle={`${dateFormat(
        props.report.period_start,
        DATE_FORMAT,
      )} – ${dateFormat(props.report.period_end, DATE_FORMAT)}`}
    >
      <div className="f5 f6-print flex justify-between mw8 center lh-copy mv4">
        <div className="tl">
          <strong>Customer information:</strong><br />
          {contract.billing_info.account_holder}<br />
          {contract.billing_info.address_line_1}<br />
          {contract.billing_info.address_line_2}<br />
          {contract.billing_info.address_city}<br />
          {contract.billing_info.address_county}<br />
          {contract.billing_info.address_postal_code}<br />
          {contract.billing_info.address_country}<br />
        </div>
        <div className="tr">
          Spirograph Limited t/a Filtru Coffee<br />
          Riverside House Kings Reach Business Park<br />
          Yew Street, Stockport<br />
          United Kingdom<br />
          SK4 2HD
        </div>
      </div>
      <table className="f5 f6-print w-100 mw8 center" cellSpacing="0">
        <thead>
          <tr>
            <th className="fw6 bb b--black-20 tl pa3 bg-white">Date</th>
            <th className="fw6 bb b--black-20 tl pa3 bg-white">Order Number</th>
            <th className="fw6 bb b--black-20 tr pa3 bg-white">Net Total</th>
            <th className="fw6 bb b--black-20 tr pa3 bg-white">
              Shipping Total
            </th>
            <th className="fw6 bb b--black-20 tr pa3 bg-white">
              Commission Due
            </th>
            <th className="fw6 bb b--black-20 tr pa3 bg-white">
              Proceeds Total
            </th>
          </tr>
        </thead>
        <tbody className="lh-copy">
          {props.report.line_items.map((line_item: LineItem) => (
            <tr>
              <td className="tl pa3 bb b--black-20">
                {dateFormat(line_item.order_date, 'mmmm d, yyyy')}
              </td>
              <td className="tl pa3 bb b--black-20 b">
                {line_item.order_number}
              </td>
              <td className="tr pa3 bb b--black-20">
                {line_item.net_total_pence}
              </td>
              <td className="tr pa3 bb b--black-20">
                {line_item.shipping_total_pence}
              </td>
              <td className="tr pa3 bb b--black-20">
                {line_item.commission_total_pence}
              </td>
              <td className="tr pa3 bb b--black-20 bg-ui-white-1">
                {getPrice(line_item.proceeds_total_pence, line_item.currency)}
              </td>
            </tr>
          ))}
          {props.report.line_items.length === 0 && (
            <tr>
              <td className="tl pa3 bb b--black-20 black-50" colSpan={6}>
                No data for this period.
              </td>
            </tr>
          )}
          <tr>
            <td colSpan={4} className="pv2 ph3">&nbsp;</td>
            <td className="tr pv2 ph3 b">Subtotal</td>
            <td className="tr pv2 ph3 bg-ui-white-1">
              {getPrice(props.report.total.proceeds_total_pence, props.report.total.currency)}
            </td>
          </tr>
          <tr>
            <td colSpan={4} className="pv2 ph3">&nbsp;</td>
            <td className="tr pv2 ph3 b">Tax</td>
            <td className="tr pv2 ph3 bg-ui-white-1">0.00</td>
          </tr>
          <tr>
            <td colSpan={4} className="pv2 ph3">&nbsp;</td>
            <td className="tr pv2 ph3 b">Total</td>
            <td className="tr pv2 ph3 bg-ui-white-1">
              {getPrice(
                (props.report.total.proceeds_total_pence + props.report.total.shipping_total_pence),
                props.report.total.currency
              )}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="mv4 lh-copy b mw8 center">
        Payment Method<br />
        {props.report.total.proceeds_total_pence > 0 ? (
          <React.Fragment>
            {getPrice(
              (props.report.total.proceeds_total_pence + props.report.total.shipping_total_pence),
              props.report.total.currency
            )} will be paid to account number ending with {contract.billing_info.account_number.slice(contract.billing_info.account_number.length - 4)}
          </React.Fragment>
        ) : (
          <React.Fragment>
            No payment due for this period.
          </React.Fragment>
        )}
      </p>
    </Page>
  );
}
